import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, authFetch, useAuth } from "@/App";
import PageLayout, { PageHeader } from "@/components/PageLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DocumentUploader from "@/components/DocumentUploader";
import { toast } from "sonner";
import { 
  FileText, Plus, Trash2, Cloud, File, FileCheck, BookOpen, 
  Download, Eye, FolderOpen, Search, Upload, Calendar,
  ArrowUpDown, Filter, Dog, Cat, Bird, Rabbit, PawPrint,
  X, FileImage, FilePlus, Clock, ChevronRight
} from "lucide-react";

// Document type config
const docTypeConfig = {
  vaccination_cert: { label: "Certificato Vaccinale", icon: FileCheck, color: "bg-emerald-100 text-emerald-700" },
  health_cert: { label: "Certificato Sanitario", icon: File, color: "bg-blue-100 text-blue-700" },
  passport: { label: "Passaporto", icon: BookOpen, color: "bg-purple-100 text-purple-700" },
  libretto: { label: "Libretto Sanitario", icon: BookOpen, color: "bg-orange-100 text-orange-700" },
  esami: { label: "Esami", icon: FileText, color: "bg-pink-100 text-pink-700" },
  other: { label: "Altro", icon: FileText, color: "bg-gray-100 text-gray-700" }
};

const speciesIcons = { dog: Dog, cat: Cat, bird: Bird, rabbit: Rabbit, other: PawPrint };

export default function Documents() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploader, setShowUploader] = useState(false);
  const [selectedPetForUpload, setSelectedPetForUpload] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPet, setFilterPet] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");
  const [viewMode, setViewMode] = useState("grid"); // grid or list

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [documentsRes, petsRes] = await Promise.all([
        authFetch(`${API}/documents`),
        authFetch(`${API}/pets`)
      ]);
      
      if (documentsRes.ok) setDocuments(await documentsRes.json());
      if (petsRes.ok) setPets(await petsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo documento?")) return;
    
    try {
      const response = await authFetch(`${API}/documents/${documentId}`, { method: 'DELETE' });
      if (response.ok) {
        setDocuments(documents.filter(d => d.document_id !== documentId));
        toast.success("Documento eliminato");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const openUploader = (pet) => {
    setSelectedPetForUpload(pet);
    setShowUploader(true);
  };

  // Filter and sort documents
  const filteredDocuments = documents
    .filter(doc => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!doc.title?.toLowerCase().includes(query) && 
            !doc.file_name?.toLowerCase().includes(query)) {
          return false;
        }
      }
      // Pet filter
      if (filterPet !== "all" && doc.pet_id !== filterPet) return false;
      // Type filter
      if (filterType !== "all" && doc.doc_type !== filterType) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "date_desc":
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case "date_asc":
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case "name_asc":
          return (a.title || "").localeCompare(b.title || "");
        case "name_desc":
          return (b.title || "").localeCompare(a.title || "");
        default:
          return 0;
      }
    });

  // Group documents by date
  const groupedByDate = filteredDocuments.reduce((acc, doc) => {
    const date = doc.created_at 
      ? new Date(doc.created_at).toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' })
      : 'Data sconosciuta';
    if (!acc[date]) acc[date] = [];
    acc[date].push(doc);
    return acc;
  }, {});

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-pulse flex flex-col items-center gap-3">
          <Cloud size={48} className="text-[#FF6B4A]" />
          <p className="text-[#86868B]">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout data-testid="documents-cloud-page">
      <PageHeader 
        title="I Miei Documenti"
        subtitle="Il cloud dei documenti dei tuoi animali"
        icon={Cloud}
        iconColor="#6366F1"
        action={pets.length > 0 && (
          <div className="flex gap-3">
            <Select 
              value={selectedPetForUpload?.pet_id || ""} 
              onValueChange={(id) => {
                const pet = pets.find(p => p.pet_id === id);
                if (pet) openUploader(pet);
              }}
            >
              <SelectTrigger className="w-48 rounded-xl bg-[#FF6B4A] text-white border-0 hover:bg-[#E05A3A]">
                <Upload size={18} className="mr-2" />
                <span>Carica documento</span>
              </SelectTrigger>
              <SelectContent>
                {pets.map((pet) => {
                  const SpeciesIcon = speciesIcons[pet.species] || PawPrint;
                  return (
                    <SelectItem key={pet.pet_id} value={pet.pet_id}>
                      <span className="flex items-center gap-2">
                        <SpeciesIcon size={16} />
                        {pet.name}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}
      />

      {/* Filters Bar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" />
            <Input
              placeholder="Cerca documenti..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl bg-[#F5F5F7] border-0"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#86868B] hover:text-[#1D1D1F]"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Pet Filter */}
          <Select value={filterPet} onValueChange={setFilterPet}>
            <SelectTrigger className="w-44 rounded-xl bg-[#F5F5F7] border-0">
              <PawPrint size={14} className="mr-2" />
              <SelectValue placeholder="Animale" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli animali</SelectItem>
              {pets.map((pet) => (
                <SelectItem key={pet.pet_id} value={pet.pet_id}>{pet.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Type Filter */}
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 rounded-xl bg-[#F5F5F7] border-0">
              <Filter size={14} className="mr-2" />
              <SelectValue placeholder="Tipo documento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              {Object.entries(docTypeConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-44 rounded-xl bg-[#F5F5F7] border-0">
              <ArrowUpDown size={14} className="mr-2" />
              <SelectValue placeholder="Ordina" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date_desc">Più recenti</SelectItem>
              <SelectItem value="date_asc">Meno recenti</SelectItem>
              <SelectItem value="name_asc">Nome (A-Z)</SelectItem>
              <SelectItem value="name_desc">Nome (Z-A)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Active Filters Summary */}
        {(filterPet !== "all" || filterType !== "all" || searchQuery) && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {filterPet !== "all" && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#FF6B4A]/10 text-[#FF6B4A] rounded-full text-sm">
                {pets.find(p => p.pet_id === filterPet)?.name}
                <button onClick={() => setFilterPet("all")}><X size={14} /></button>
              </span>
            )}
            {filterType !== "all" && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                {docTypeConfig[filterType]?.label}
                <button onClick={() => setFilterType("all")}><X size={14} /></button>
              </span>
            )}
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                "{searchQuery}"
                <button onClick={() => setSearchQuery("")}><X size={14} /></button>
              </span>
            )}
            <button 
              onClick={() => { setFilterPet("all"); setFilterType("all"); setSearchQuery(""); }}
              className="text-sm text-[#86868B] hover:text-[#1D1D1F]"
            >
              Cancella filtri
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <FileText size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1D1D1F]">{documents.length}</p>
              <p className="text-xs text-[#86868B]">Documenti totali</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <PawPrint size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1D1D1F]">{pets.length}</p>
              <p className="text-xs text-[#86868B]">Animali</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <FileCheck size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1D1D1F]">
                {documents.filter(d => d.doc_type === 'vaccination_cert').length}
              </p>
              <p className="text-xs text-[#86868B]">Certificati vaccini</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Calendar size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1D1D1F]">
                {documents.filter(d => {
                  const date = new Date(d.created_at);
                  const now = new Date();
                  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                }).length}
              </p>
              <p className="text-xs text-[#86868B]">Questo mese</p>
            </div>
          </div>
        </div>
      </div>

      {/* Documents List */}
      {filteredDocuments.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-black/5">
          <Cloud className="mx-auto text-[#D1D1D6] mb-4" size={64} />
          <h3 className="font-semibold text-[#1D1D1F] text-xl mb-2">
            {documents.length === 0 ? "Nessun documento caricato" : "Nessun risultato"}
          </h3>
          <p className="text-[#86868B] mb-6 max-w-md mx-auto">
            {documents.length === 0 
              ? "Inizia a caricare i documenti sanitari dei tuoi animali. Saranno sempre accessibili ovunque." 
              : "Prova a modificare i filtri di ricerca"}
          </p>
          {pets.length > 0 && documents.length === 0 && (
            <Button 
              onClick={() => openUploader(pets[0])}
              className="bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl"
            >
              <Upload size={18} className="mr-2" />
              Carica il primo documento
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByDate).map(([date, docs]) => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-3">
                <Clock size={16} className="text-[#86868B]" />
                <h3 className="text-sm font-semibold text-[#86868B] uppercase tracking-wide">{date}</h3>
                <span className="text-xs text-[#AEAEB2]">({docs.length} documenti)</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {docs.map((doc) => {
                  const typeConfig = docTypeConfig[doc.doc_type] || docTypeConfig.other;
                  const TypeIcon = typeConfig.icon;
                  const pet = pets.find(p => p.pet_id === doc.pet_id);
                  const SpeciesIcon = pet ? (speciesIcons[pet.species] || PawPrint) : PawPrint;
                  
                  // Extract date from filename if available
                  const fileName = doc.file_name || doc.title || "Documento";
                  
                  return (
                    <div 
                      key={doc.document_id}
                      className="bg-white rounded-2xl p-4 shadow-sm border border-black/5 hover:shadow-md transition-all group"
                      data-testid={`document-card-${doc.document_id}`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className={`w-12 h-12 rounded-xl ${typeConfig.color} flex items-center justify-center flex-shrink-0`}>
                          <TypeIcon size={24} />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-[#1D1D1F] truncate">{doc.title || fileName}</h4>
                          <p className="text-sm text-[#86868B] truncate">{fileName}</p>
                          
                          {/* Pet badge */}
                          {pet && (
                            <div className="flex items-center gap-1.5 mt-2">
                              <div className="w-5 h-5 rounded-full overflow-hidden bg-[#F5F5F7] flex items-center justify-center">
                                {pet.photo_url ? (
                                  <img src={pet.photo_url} alt={pet.name} className="w-full h-full object-cover" />
                                ) : (
                                  <SpeciesIcon size={12} className="text-[#86868B]" />
                                )}
                              </div>
                              <span className="text-xs text-[#86868B]">{pet.name}</span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {doc.file_url && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => window.open(doc.file_url.startsWith('/') ? `${API.replace('/api', '')}${doc.file_url}` : doc.file_url, '_blank')}
                              className="h-8 w-8 rounded-lg text-[#86868B] hover:text-[#1D1D1F]"
                            >
                              <Eye size={16} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteDocument(doc.document_id)}
                            className="h-8 w-8 rounded-lg text-red-500 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                      
                      {/* File type badge */}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-black/5">
                        <span className={`text-xs px-2 py-1 rounded-lg ${typeConfig.color}`}>
                          {typeConfig.label}
                        </span>
                        <span className="text-xs text-[#AEAEB2]">
                          {new Date(doc.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploader} onOpenChange={setShowUploader}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={20} className="text-[#FF6B4A]" />
              Carica documento per {selectedPetForUpload?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPetForUpload && (
            <DocumentUploader
              petId={selectedPetForUpload.pet_id}
              petName={selectedPetForUpload.name}
              onUploadComplete={() => {
                setShowUploader(false);
                fetchData();
                toast.success("Documento caricato!");
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
